import { CsvExportButton, type CsvExportState } from '../../components/CsvExport';
import type { FinanceReport } from '../../../shared/types';
import { formatMoney, formatMoneyExact } from '../../components/primitives';
import { fmtCell, QUARTER_NOTES } from '../../lib/financeQuarters';
import { useIsMobile } from '../../lib/useIsMobile';
import { FinanceReportCards } from '../../components/FinanceReportCards';
import { useSelectedFY } from './hooks';


// ── Reports tab ───────────────────────────────────────────────────────────

// QUARTER_NOTES and fmtCell come from lib/financeQuarters so the desktop
// table and the mobile cards format from one source (review C-2).
export function ReportsTab({
  report,
  currentQuarterKey,
  fyKey,
  basExport,
}: {
  report: FinanceReport | null;
  currentQuarterKey: string;
  fyKey: string;
  /** Lifted to the page so the mobile cards, the desktop group link and the
      page menu all download the same CSV. */
  basExport: CsvExportState;
}) {
  const isMobile = useIsMobile();
  const fy = useSelectedFY(fyKey);
  if (!report) return null;
  const quarters = report.quarters.filter((q) => q.key.startsWith(fy));
  if (quarters.length === 0) {
    return <div className="empty-note">No dated records for {fy} yet.</div>;
  }
  const latest = quarters[0]; // quarters arrive newest-first
  const basPosition = latest.gst_collected_cents - latest.gst_paid_cents;
  const qNote = QUARTER_NOTES[latest.key.split(' ')[1]];

  // ≤880px (§8.1 C): the BAS card and the seven-column table become cards.
  if (isMobile) {
    return (
      <FinanceReportCards
        quarters={quarters}
        currentQuarterKey={currentQuarterKey}
        fy={fy}
        onExportCsv={basExport.download}
      />
    );
  }

  return (
    <>
      <div className="bas-card">
        <div className="bas-block">
          <span className="lbl">{latest.key} · GST collected</span>
          <span className="val">{formatMoneyExact(latest.gst_collected_cents)}</span>
        </div>
        <div className="bas-block">
          <span className="lbl">GST paid</span>
          <span className="val gold">{formatMoneyExact(latest.gst_paid_cents)}</span>
        </div>
        <div className="bas-block">
          <span className="lbl">BAS position</span>
          <span className="val accent">{formatMoneyExact(basPosition)}</span>
        </div>
        {qNote && (
          <div className="bas-note">
            Figures follow the Australian FY — {latest.key.split(' ')[1]} runs {qNote.months}, due {qNote.due}.
          </div>
        )}
      </div>

      <div className="group">
        <div className="group-head">
          <span className="group-title">Quarterly — Australian FY</span>
          <span className="group-count">{fy}</span>
          <CsvExportButton state={basExport} quiet />
        </div>
        <table className="report-table">
          <thead>
            <tr>
              <th>Quarter</th>
              <th>Invoiced</th>
              <th>Paid</th>
              <th>GST collected</th>
              <th>Expenses</th>
              <th>GST paid</th>
              <th>Net</th>
            </tr>
          </thead>
          <tbody>
            {quarters.map((q) => (
              <tr key={q.key} className={q.key === currentQuarterKey ? 'current' : undefined}>
                <td>{q.key}</td>
                <td>{formatMoney(q.invoiced_cents)}</td>
                <td className="paid">{formatMoney(q.paid_cents)}</td>
                <td>{fmtCell(q.gst_collected_cents)}</td>
                <td>{fmtCell(q.expenses_cents)}</td>
                <td>{fmtCell(q.gst_paid_cents)}</td>
                <td className="net">{fmtCell(q.net_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
